import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { INDUSTRIES, PRODUCTS } from "@/data/content";
import { ProductIcon } from "@/lib/icons";
import { PrimaryLink, GhostLink, Section } from "@/components/shared";

const IndustryDetail = () => {
  const { slug } = useParams();
  const { t, pick } = useLanguage();
  const industry = INDUSTRIES.find((i) => i.slug === slug);
  if (!industry) return <Navigate to="/industries" replace />;

  const stack = industry.stack.map((s) => PRODUCTS.find((p) => p.slug === s)).filter(Boolean);
  const others = INDUSTRIES.filter((i) => i.slug !== slug).slice(0, 4);

  return (
    <div data-testid={`industry-page-${slug}`}>
      <section className="relative py-20 sm:py-28 px-5 sm:px-8 grid-bg overflow-hidden">
        <div className="absolute -top-32 left-1/3 w-[700px] h-[500px] glow-orb pointer-events-none" />
        <div className="max-w-5xl mx-auto relative">
          <Link to="/industries" data-testid="back-to-industries" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white mb-10 transition-colors">
            <ArrowLeft className="w-4 h-4" /> {t("common.backToIndustries")}
          </Link>
          <span className="w-14 h-14 rounded-2xl bg-indigo-500/15 flex items-center justify-center mb-6">
            <ProductIcon name={industry.icon} className="w-7 h-7 text-indigo-400" />
          </span>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold font-heading tracking-tight text-white mb-5">{pick(industry.name)}</h1>
          <p className="text-base md:text-lg text-slate-300 leading-relaxed max-w-2xl mb-10">{pick(industry.desc)}</p>
          <div className="flex flex-wrap gap-3">
            <PrimaryLink to="/signup" testId="industry-start-free">{t("common.startFree")}</PrimaryLink>
            <GhostLink to="/contact" testId="industry-book-demo">{t("common.bookDemo")}</GhostLink>
          </div>
        </div>
      </section>

      <Section eyebrow={t("common.recommended").toUpperCase()} title="" center={false} className="bg-[#05070B] py-16">
        <p className="text-xs font-mono2 uppercase tracking-[0.25em] text-cyan-400 mb-6 -mt-8">{t("common.recommended")}</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {stack.map((p) => (
            <Link
              key={p.slug}
              to={`/products/${p.slug}`}
              data-testid={`stack-${p.slug}`}
              className={`card-surface card-surface-hover rounded-2xl p-5 border border-slate-800/80 ${p.accent.border} group`}
            >
              <div className="flex items-center gap-3 mb-3">
                <span className={`w-10 h-10 rounded-xl bg-gradient-to-br ${p.accent.grad} flex items-center justify-center`}>
                  <ProductIcon name={p.icon} className="w-5 h-5 text-white" />
                </span>
                <div>
                  <p className="font-heading font-semibold text-white text-sm">{p.name}</p>
                  <p className={`text-[10px] font-mono2 uppercase tracking-widest ${p.accent.text}`}>{pick(p.tagline)}</p>
                </div>
              </div>
              <span className={`inline-flex items-center gap-2 text-xs font-semibold ${p.accent.text} group-hover:gap-3 transition-all`}>
                {t("common.exploreProduct")} <ArrowRight className="w-3.5 h-3.5" />
              </span>
            </Link>
          ))}
        </div>
      </Section>

      <section className="py-16 px-5 sm:px-8 border-t border-slate-800/60">
        <div className="max-w-5xl mx-auto">
          <div className="flex flex-wrap gap-2.5">
            {others.map((i) => (
              <Link key={i.slug} to={`/industries/${i.slug}`} data-testid={`other-industry-${i.slug}`} className="text-sm text-slate-300 bg-slate-800/70 border border-slate-700 hover:border-indigo-500/50 rounded-full px-4 py-2 transition-colors">
                {pick(i.name)}
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default IndustryDetail;
